import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import MainHeading from '@/components/heading/MainHeading';
import { ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { products } from '@/core/global-data/products.data';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '@/lib/i18n/useLocale';

export default function Products() {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });
    const [searchParams] = useSearchParams();
    const social = searchParams.get('social');

    return (
        <section ref={ref} className="mt-[100px]">
            <div>
                <MainHeading>{t('products.title')}</MainHeading>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-center gap-8">
                    {products.map((product, index) => (
                        <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 50 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: index * 0.2 }}
                            className="bg-[#ffffff] dark:bg-[#082743] rounded-2xl overflow-hidden shadow-md border border-border dark:border-[#0E4272] flex flex-col justify-between"
                        >
                            {product.cardSvg && (
                                <div
                                    style={{
                                        background: 'linear-gradient(90deg, #457FCA 0%, #5691C8 100%)',
                                    }}
                                    className="flex justify-center max-h-[214px] h-full"
                                >
                                    {/* Render cardSvg as a React component */}
                                    <product.cardSvg />
                                </div>
                            )}
                            <div className="p-4">
                                <h3 className="text-nav mb-2 font-bold text-[16px] leading-6 tracking-normal text-right">
                                    {t(`products.${product.id}.title`)}
                                </h3>
                                <p className="text-carddescription font-[400] text-[16px] leading-[24px] tracking-normal text-right">
                                    {t(`products.${product.id}.cardDescription`)}
                                </p>
                            </div>
                            <Link
                                to={localizedPath(`/products/${product.id}`, {
                                    search: social ? `social=${social}` : undefined,
                                })}
                            >
                                <div className="p-5 pt-0 text-right">
                                    <motion.button
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="flex items-center gap-2 text-sm bg-blue-100 dark:bg-[#1e3a5f] dark:[&_svg]:brightness-0 dark:[&_svg]:invert text-nav font-medium px-4 py-2 rounded-full hover:bg-blue-200 transition"
                                    >
                                        {t('products.more')}
                                        <ArrowLeft className="w-4 h-4" />
                                    </motion.button>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
